import {
  AfarNetworkList,
  NetworkRemovalImpact,
  GuestProvisioningGrant,
  GuestActionGrant,
  type GuestActionRequest,
  type GuestProvisioningRequest,
  CreatedOrganization,
  NetworkBrokeredConnection,
  NetworkEnrollmentCode,
  OrganizationView,
  SentOrganizationInvitation,
  SshMachineSetupStarted,
  type CloudWorkspaceEndpoint,
  type SshMachineSetup,
  type MemberEnvironmentLimits,
  type OrganizationRole,
} from "@afar-research/contracts";
import { connectCall, type ConnectFetch } from "@afar-research/shared/connect";
import * as Schema from "effect/Schema";

import { redeemBrokeredGrant } from "./cloudWorkspaceConnection.ts";

const Done = Schema.Unknown;

/** AFAR Connect's network and Organization routes (ADR-0146, ADR-0148). */
export function networkClient(input: { connectOrigin: string; fetch: ConnectFetch }) {
  const call = <A>(path: string, token: string, body: unknown, schema: Schema.Decoder<A, never>) =>
    connectCall(input.fetch, input.connectOrigin, path, token, body, schema);
  const id = encodeURIComponent;
  return {
    networks: (token: string) => call("/v1/networks", token, undefined, AfarNetworkList),
    createEnrollmentCode: (token: string, networkId: string) =>
      call(`/v1/networks/${id(networkId)}/enrollment-codes`, token, {}, NetworkEnrollmentCode),
    guestProvisioning: (
      token: string,
      networkId: string,
      parentMachineId: string,
      request: GuestProvisioningRequest,
    ) =>
      call(
        `/v1/networks/${id(networkId)}/machines/${id(parentMachineId)}/guest-provisioning`,
        token,
        request,
        GuestProvisioningGrant,
      ),
    guestAction: (
      token: string,
      networkId: string,
      parentMachineId: string,
      request: GuestActionRequest,
    ) =>
      call(
        `/v1/networks/${id(networkId)}/machines/${id(parentMachineId)}/guest-actions`,
        token,
        request,
        GuestActionGrant,
      ),
    removalImpact: (token: string, networkId: string, machineId: string) =>
      call(
        `/v1/networks/${id(networkId)}/machines/${id(machineId)}/removal-impact`,
        token,
        undefined,
        NetworkRemovalImpact,
      ),
    removeMachine: (token: string, networkId: string, machineId: string) =>
      call(`/v1/networks/${id(networkId)}/machines/${id(machineId)}/remove`, token, {}, Done),
    setMachineEnabled: (token: string, networkId: string, machineId: string, enabled: boolean) =>
      call(
        `/v1/networks/${id(networkId)}/machines/${id(machineId)}/enabled`,
        token,
        { enabled },
        Done,
      ),
    setUpOverSsh: (token: string, networkId: string, setup: SshMachineSetup) =>
      call(`/v1/networks/${id(networkId)}/ssh-setup`, token, setup, SshMachineSetupStarted),
    setLimits: (
      token: string,
      networkId: string,
      machineId: string,
      limits: MemberEnvironmentLimits,
    ) =>
      call(`/v1/networks/${id(networkId)}/machines/${id(machineId)}/limits`, token, limits, Done),
    memberEnvironmentAction: (
      token: string,
      networkId: string,
      machineId: string,
      accountId: string,
      action: "stop" | "delete",
    ) =>
      call(
        `/v1/networks/${id(networkId)}/machines/${id(machineId)}/member-environments/${id(accountId)}/${action}`,
        token,
        {},
        Done,
      ),
    createOrganization: (token: string, name: string) =>
      call("/v1/organizations", token, { name }, CreatedOrganization),
    organization: (token: string, organizationId: string) =>
      call(`/v1/organizations/${id(organizationId)}`, token, undefined, OrganizationView),
    invite: (token: string, organizationId: string, email: string, role: OrganizationRole) =>
      call(
        `/v1/organizations/${id(organizationId)}/invitations`,
        token,
        { email, role },
        SentOrganizationInvitation,
      ),
    revokeInvitation: (token: string, organizationId: string, invitationId: string) =>
      call(
        `/v1/organizations/${id(organizationId)}/invitations/${id(invitationId)}/revoke`,
        token,
        {},
        Done,
      ),
    acceptInvitation: (token: string, invitation: string) =>
      call(
        "/v1/organizations/invitations/accept",
        token,
        { token: invitation },
        Schema.Struct({ organizationId: Schema.String }),
      ),
    changeRole: (
      token: string,
      organizationId: string,
      accountId: string,
      role: OrganizationRole,
    ) =>
      call(
        `/v1/organizations/${id(organizationId)}/members/${id(accountId)}/role`,
        token,
        { role },
        Done,
      ),
    removeMember: (token: string, organizationId: string, accountId: string) =>
      call(
        `/v1/organizations/${id(organizationId)}/members/${id(accountId)}/remove`,
        token,
        {},
        Done,
      ),
    deleteOrganization: (token: string, organizationId: string) =>
      call(`/v1/organizations/${id(organizationId)}/delete`, token, {}, Done),
  };
}

/**
 * What a client asks Connect to reach: a Cloud Workspace by its id (Connect starts it when it is
 * stopped), or a Self-Hosted Machine by its network and machine (ADR-0150).
 */
export type NetworkConnectTarget =
  | { readonly workspaceId: string }
  | { readonly networkId: string; readonly machineId: string };

/**
 * Which target an Environment id reconnects through. A remembered network route names a
 * Self-Hosted Machine; every other Connect Environment is a Cloud Workspace, whose Environment id
 * is its workspace id. A Cloud Workspace is never remembered as a machine: the network listing
 * shows it as a workspace, not a machine.
 */
export function networkConnectTarget(
  environmentId: string,
  route: NetworkMachineRoute | undefined,
): NetworkConnectTarget {
  return route === undefined
    ? { workspaceId: environmentId }
    : { networkId: route.networkId, machineId: route.machineId };
}

/**
 * Reaching an Environment on an AFAR Network through the Network Gateway (ADR-0147, ADR-0150):
 * Connect brokers the introduction with this installation's credential, and the grant is redeemed
 * with the Environment directly, through the gateway. The endpoint carries a short-lived route
 * ticket, so every attempt asks Connect again.
 */
export async function openNetworkConnection(input: {
  readonly fetch: ConnectFetch;
  readonly connectOrigin: string;
  readonly installation: {
    readonly credential: string;
    readonly publicKey: string;
    readonly privateKey: string;
  };
  readonly clientLabel: string;
  readonly target: NetworkConnectTarget;
}): Promise<{
  readonly networkId: string;
  readonly machineId: string;
  readonly environmentId: string;
  readonly label: string;
  readonly endpoint: CloudWorkspaceEndpoint;
}> {
  const brokered = await connectCall(
    input.fetch,
    input.connectOrigin,
    "/v1/networks/connect",
    input.installation.credential,
    { ...input.target, clientPublicKey: input.installation.publicKey },
    NetworkBrokeredConnection,
    // Connect waits up to five minutes for a member's new Member Environment to boot and join.
    320_000,
  );
  // The Environment that answered has to be the workspace asked for. A credential minted elsewhere
  // is redeemable only there, which is a confusing way to discover a mix-up.
  if ("workspaceId" in input.target && brokered.environmentId !== input.target.workspaceId) {
    throw new Error(
      `AFAR Connect introduced ${brokered.environmentId} when ${input.target.workspaceId} was asked for.`,
    );
  }
  const endpoint = await redeemBrokeredGrant({
    fetch: input.fetch,
    endpoint: brokered.endpoint,
    credential: brokered.credential,
    installation: input.installation,
    clientLabel: input.clientLabel,
  });
  return {
    networkId: brokered.networkId,
    machineId: brokered.machineId,
    environmentId: brokered.environmentId,
    label: brokered.label,
    endpoint,
  };
}

/** Which network and machine each Environment this device reached through a network belongs to. */
export interface NetworkMachineRoute {
  readonly networkId: string;
  readonly machineId: string;
  readonly label: string;
}

const NetworkMachineRoutes = Schema.Record(
  Schema.String,
  Schema.Struct({ networkId: Schema.String, machineId: Schema.String, label: Schema.String }),
);
const decodeRoutes = Schema.decodeUnknownOption(Schema.fromJsonString(NetworkMachineRoutes));

export const networkMachineRoutesKey = (connectOrigin: string) =>
  `afar-connect:network-machines:v1:${connectOrigin}`;

export function networkMachineRoutes(
  storage: Pick<Storage, "getItem" | "setItem">,
  connectOrigin: string,
) {
  const key = networkMachineRoutesKey(connectOrigin);
  const read = (): Record<string, NetworkMachineRoute> => {
    const text = storage.getItem(key);
    if (text === null) return {};
    const decoded = decodeRoutes(text);
    return decoded._tag === "Some" ? { ...decoded.value } : {};
  };
  return {
    list: () => Object.entries(read()).map(([id, route]) => ({ id, ...route })),
    get: (environmentId: string): NetworkMachineRoute | undefined => read()[environmentId],
    remember: (environmentId: string, route: NetworkMachineRoute) =>
      storage.setItem(key, JSON.stringify({ ...read(), [environmentId]: route })),
    forget: (environmentId: string) => {
      const routes = read();
      delete routes[environmentId];
      storage.setItem(key, JSON.stringify(routes));
    },
  };
}
